import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { Meteor } from 'meteor/meteor';
import { withTracker } from 'meteor/react-meteor-data';
import { get, isEmpty, isNil } from 'lodash';
import turf from '@turf/turf';

import * as atlasOps from '../../../api/Atlas/atlas-operations';
import * as taskOps from '../../../api/Tasks/task-operations';
import TelemetryConnections from '../../../api/TelemetryConnections/TelemetryConnections';

import aiTask from '../../../modules/tasks/aiTask';

import AITaskConfiguration from '../../components/AITaskConfiguration';
import Loading from '../../components/Loading';
import MapContainerAIAtlas from '../../containers/MapContainerAIAtlas';
import TaskMonitorContainer from '../../containers/TaskMonitorContainer';

import {
  GridContainer,
  Grid,
  GridLeftChild,
  GridRightChild,
  GridTopRightChild,
  GridBottomRightChild,
  GridTaskMonitorContainer
} from './styles';

const PER_PAGE = 5;
const HEADER_HEIGHT = 57; // When it is all in the same line
const PAGINATION_HEIGHT = 46;
const CELL_HEIGHT = 62;
const BBOX_PATH = ['nodes', '0', 'paramList', '3', 'value'];
const ENTITIES_PATH = ['nodes', '2', 'paramList', '0', 'value'];

class AIAtlas extends Component {
  tableElement = React.createRef();

  state = {
    aiInterestArea: {},
    isTableListLoaded: false,
    itemsPerPage: PER_PAGE,
    selectedTaskEntities: []
  };

  componentDidMount() {
    window.addEventListener('resize', this.updateDimensions);
  }

  componentDidUpdate() {
    const { isTableListLoaded } = this.state;
    if (!isTableListLoaded && !isNil(this.tableElement.current)) this.updateDimensions();
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.updateDimensions);
  }

  setAIInterestArea = newAIInterestArea => this.setState({ aiInterestArea: newAIInterestArea });

  selectTask = task => {
    const bbox = get(task, BBOX_PATH, '');
    const selectedTaskEntities = get(task, ENTITIES_PATH, []);

    let newAIInterestArea = {};
    if (!isEmpty(bbox)) newAIInterestArea = turf.bboxPolygon(JSON.parse(bbox));

    this.setState({ aiInterestArea: newAIInterestArea, selectedTaskEntities });
  };

  updateDimensions = () => {
    const height =
      get(this, 'tableElement.current.offsetHeight', 0) - (PAGINATION_HEIGHT + HEADER_HEIGHT);

    let itemsPerPage = Math.floor(height / CELL_HEIGHT);
    if (itemsPerPage < 1) itemsPerPage = 1;

    this.setState({ isTableListLoaded: true, itemsPerPage });
  };

  render() {
    const {
      atlas,
      deployEnv,
      gmsConnection,
      gridBackgroundColor,
      isPreview,
      isPublic,
      loading
    } = this.props;
    const { aiInterestArea, itemsPerPage, selectedTaskEntities } = this.state;

    if (loading) return <Loading />;
    return (
      <>
        <GridContainer backgroundcolor={gridBackgroundColor} fullScreen>
          <Grid>
            <GridLeftChild xs={12} md={8}>
              <MapContainerAIAtlas
                aiInterestArea={aiInterestArea}
                atlas={atlas}
                deployEnv={deployEnv}
                setAIInterestArea={this.setAIInterestArea}
              />
            </GridLeftChild>
            <GridRightChild xs={12} md={4}>
              <GridTopRightChild height="50%">
                <AITaskConfiguration
                  aiInterestArea={aiInterestArea}
                  atlas={atlas}
                  gmsConnection={gmsConnection}
                  isPreview={isPreview}
                  isPublic={isPublic}
                  selectedTaskEntities={selectedTaskEntities}
                />
              </GridTopRightChild>
              <GridBottomRightChild height="50%">
                <GridTaskMonitorContainer ref={this.tableElement}>
                  <TaskMonitorContainer
                    atlasId={atlasOps.getId(atlas)}
                    gmsConnection={gmsConnection}
                    isAtlas
                    isPreview={isPreview}
                    isPublic={isPublic}
                    itemsPerPage={itemsPerPage}
                    taskLabel={taskOps.getLabel(aiTask({}))}
                    selectTask={this.selectTask}
                  />
                </GridTaskMonitorContainer>
              </GridBottomRightChild>
            </GridRightChild>
          </Grid>
        </GridContainer>
      </>
    );
  }
}

AIAtlas.defaultProps = {
  atlas: {},
  deployEnv: '',
  gmsConnection: {},
  gridBackgroundColor: '#98c1de',
  isPreview: false,
  isPublic: false
};

AIAtlas.propTypes = {
  atlas: PropTypes.object,
  deployEnv: PropTypes.string,
  gmsConnection: PropTypes.object,
  gridBackgroundColor: PropTypes.string,
  isPreview: PropTypes.bool,
  isPublic: PropTypes.bool,
  loading: PropTypes.bool.isRequired
};

export default withTracker(({ atlas }) => {
  let gmsConnectionId = '';
  let subscription = {};
  if (!isNil(atlas)) {
    gmsConnectionId = atlasOps.getGMSConnection(atlas);
    subscription = Meteor.subscribe('gmsConnections.public', gmsConnectionId);
  }
  return {
    loading: !isNil(atlas) && !subscription.ready(),
    gmsConnection: TelemetryConnections.findOne(gmsConnectionId)
  };
})(AIAtlas);
